import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import { apiWrapper } from './reduxUtils';
import { getAllApi, getDataByIdApi, postApi, putApi, delApi } from 'api/crud';
import {
  convertRequestParams,
  convertResponseData,
  PRIMARY_KEY,
} from './dataProvider';
import {
  CreatePayloadType,
  DelPayloadType,
  EditPayloadType,
  GetAllPayloadType,
} from '@redux/crudCreator/types';

export const getAll = (
  resource: string,
  primaryKey = PRIMARY_KEY,
  customApiResource?: string,
) =>
  createAsyncThunk(
    `${resource}/getAll`,
    async (payload: GetAllPayloadType, thunkAPI) => {
      try {
        const { data = {}, options = {} } = payload;
        const { pageSize, page, includes, filters } =
          thunkAPI.getState()[resource];

        const convertRequest = convertRequestParams('GET_ALL', {
          limit: pageSize,
          offset: pageSize * (page - 1),
          filters,
          includes,
          ...data,
        });
        const response = await apiWrapper(
          { isShowProgress: options.isShowProgress },
          getAllApi,
          options.customApiResource || customApiResource || resource,
          convertRequest,
        );
        const result = convertResponseData('GET_ALL', response, { primaryKey });
        if (result.data) {
          return {
            data: {
              numberOfPages: Math.round(result.total / pageSize),
              ...result,
            },
            options,
          };
        }
        return thunkAPI.rejectWithValue({ data: response, options });
      } catch (error) {
        console.log(error);
        return thunkAPI.rejectWithValue({});
      }
    },
  );

export const getDataById = (
  resource,
  primaryKey = PRIMARY_KEY,
  customApiResource,
) =>
  createAsyncThunk(
    `${resource}/getDataById`,
    async (payload: GetAllPayloadType, thunkAPI) => {
      const { data, options = { extraParams: {}, isRequestApi: true } } =
        payload;
      try {
        if (!options.isRequestApi) {
          return { data };
        }
        const response = await apiWrapper(
          { isShowProgress: options.isShowProgress },
          getDataByIdApi,
          options.customApiResource || customApiResource || resource,
          data[PRIMARY_KEY],
          options.suffix,
          options.extraParams,
        );
        const result = convertResponseData('GET_BY_ID', response, {
          primaryKey,
        });
        if (result) {
          return { data: result };
        }
        return thunkAPI.rejectWithValue({ data: result, options });
      } catch (error) {
        return thunkAPI.rejectWithValue({ data, options });
      }
    },
  );

export const edit = (resource, primaryKey = PRIMARY_KEY, customApiResource) =>
  createAsyncThunk(
    `${resource}/edit`,
    async (payload: EditPayloadType, thunkAPI) => {
      const { data, options = {} } = payload;
      try {
        const requestData = convertRequestParams('EDIT', data);
        const response = await apiWrapper(
          options,
          putApi,
          options.customApiResource || customApiResource || resource,
          data[PRIMARY_KEY],
          options.suffix,
          requestData,
        );
        const result = convertResponseData('EDIT', response, { primaryKey });
        if (result) {
          return { data: { ...data, ...result }, options };
        }
        return thunkAPI.rejectWithValue({
          error: true,
          data: { ...data, ...response },
        });
      } catch (error) {
        return thunkAPI.rejectWithValue({ data, error: true, options });
        //
      }
    },
  );

export const create = (resource, primaryKey, customApiResource) =>
  createAsyncThunk(
    `${resource}/create`,
    async (payload: CreatePayloadType, thunkAPI) => {
      const { data, options = {} } = payload;
      try {
        const response = await apiWrapper(
          { isShowProgress: options.isShowProgress, ...options },
          postApi,
          options.customApiResource || customApiResource || resource,
          options.suffix,
          data,
        );
        const result = convertResponseData('CREATE', response);
        if (result) {
          if (options.onSuccessAction) {
            options.onSuccessAction(result, data);
          }
          return { data: result, success: true };
        }
        return thunkAPI.rejectWithValue({ error: true, data: response });
      } catch (error) {
        return thunkAPI.rejectWithValue({ data: error, error: true });
      }
    },
  );

export const del = (resource, primaryKey = PRIMARY_KEY, customApiResource) =>
  createAsyncThunk(
    `${resource}/del`,
    async (payload: DelPayloadType, thunkAPI) => {
      const { data, options = {} } = payload;
      try {
        await apiWrapper(
          { isShowProgress: true, ...options },
          delApi,
          options.customApiResource || customApiResource || resource,
          data.path || data[primaryKey],
          options?.suffix,
          options?.hasNotUseId,
          data?.params,
        );
        // const result = convertResponseData('DELETE', response, { primaryKey });
        return { data, options };
      } catch (error) {
        return thunkAPI.rejectWithValue({ options, data, error });
      }
    },
  );

export const updatePosition = (resource) =>
  createAsyncThunk(
    `${resource}/updatePosition`,
    async (payload: CreatePayloadType, thunkAPI) => {
      try {
        return payload.data;
      } catch (error) {
        return thunkAPI.rejectWithValue({ data: error, error: true });
      }
    },
  );
export const clearCurrent = (resource) =>
  createAction(`${resource}/clearCurrent`);

export const resetData = (resource) => createAction(`${resource}/resetData`);

export const setItem = (resource) => createAction(`${resource}/setItem`);

export const toogleModal = (resource) =>
  createAction(`${resource}/toogleModal`);

export const setSelectedRowKeys = (resource) =>
  createAction(`${resource}/setSelectedRowKeys`);

export const makeActions = (
  resource,
  primaryKey?: string,
  customApiResource?: string,
) => ({
  getAll: getAll(resource, primaryKey, customApiResource),
  getDataById: getDataById(resource, primaryKey, customApiResource),
  edit: edit(resource, primaryKey, customApiResource),
  create: create(resource, primaryKey, customApiResource),
  del: del(resource, primaryKey, customApiResource),
  clearCurrent: clearCurrent(resource),
  resetData: resetData(resource),
  setItem: setItem(resource),
  updatePosition: updatePosition(resource),
  setSelectedRowKeys: setSelectedRowKeys(resource),
  toogleModal: toogleModal(resource),
});
